// frontend-react/src/components/LineupHistory/LineupHistory.js
// Lineup history viewer with timeline and diff functionality

import React, { useState, useEffect } from 'react';
import TeamPageHeader from '../common/TeamPageHeader';
import { 
  AlertCircle, 
  RefreshCw, 
  Calendar,
  ChevronLeft,
  ChevronRight,
  GitCompare,
  Users,
  Clock
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { 
  fetchLineupHistory,
  fetchAvailableSnapshotDates,
  getDaysAgo,
  getTodayDate,
  formatDisplayDate,
  formatShortDate
} from '../../services/lineupHistoryApi';
import './LineupHistory.css';
import PlayerLink from '../PlayerLink';

/**
 * LineupHistory Component
 * Displays lineup snapshots over time with diff functionality
 * 
 * @param {string} leagueId - League UUID
 * @param {string} teamId - Team UUID
 * @param {object} league - League object
 */
const LineupHistory = ({ leagueId, teamId, league, userTeam }) => {
  // State management
  const [startDate, setStartDate] = useState(null); // Will be set after loading available dates
  const [endDate, setEndDate] = useState(null); // Will be set after loading available dates
  const [lineups, setLineups] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [datesLoading, setDatesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'compare'
  const [compareDate1, setCompareDate1] = useState(null);
  const [compareDate2, setCompareDate2] = useState(null);

  // Load available dates on mount
  useEffect(() => {
    if (leagueId && teamId) {
      loadAvailableDates();
    }
  }, [leagueId, teamId]);

  // Load lineup history when date range changes
  useEffect(() => {
    if (leagueId && teamId && startDate && endDate) {
      loadLineupHistory();
    }
  }, [leagueId, teamId, startDate, endDate]);

  /**
   * Load available snapshot dates
   */
  const loadAvailableDates = async () => {
    try {
      setDatesLoading(true);
      const response = await fetchAvailableSnapshotDates(leagueId, teamId, league?.current_season);
      
      if (response.success && response.dates) {
        const sortedDates = [...response.dates].sort((a, b) => b.localeCompare(a));
        setAvailableDates(sortedDates);
        
        // Set default date range to most recent snapshot date (like box score page)
        if (sortedDates.length > 0) {
          const mostRecentDate = sortedDates[0];
          // Set end date to most recent snapshot
          setEndDate(mostRecentDate);
          // Set start date to 14 days before most recent snapshot
          const startDateObj = new Date(mostRecentDate);
          startDateObj.setDate(startDateObj.getDate() - 14);
          setStartDate(startDateObj.toISOString().split('T')[0]);
        } else {
          // Fallback if no dates available
          setStartDate(getDaysAgo(14));
          setEndDate(getTodayDate());
        }
        
        // Set default compare dates to most recent two
        if (sortedDates.length >= 2) {
          setCompareDate1(sortedDates[1]); // Second most recent
          setCompareDate2(sortedDates[0]); // Most recent
        } else if (sortedDates.length === 1) {
          setCompareDate1(sortedDates[0]);
          setCompareDate2(sortedDates[0]);
        }
      }
    } catch (err) {
      console.error('Failed to load available dates:', err);
      // Fallback on error
      setStartDate(getDaysAgo(14));
      setEndDate(getTodayDate());
    } finally {
      setDatesLoading(false);
    }
  };

  /**
   * Load lineup history for date range
   */
  const loadLineupHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchLineupHistory(leagueId, teamId, startDate, endDate);

      if (response.success) {
        setLineups(response.lineups || []);
        
        if (response.lineups.length === 0) {
          setError('No lineup snapshots found for this date range.');
        }
      } else {
        setError(response.detail || 'Failed to load lineup history');
      }
    } catch (err) {
      console.error('Error loading lineup history:', err);
      setError(err.message || 'Failed to load lineup history');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Shift date range backward
   */
  const handlePrevious = () => {
    const daysDiff = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
    const newStartDate = getDaysAgo(daysDiff * 2);
    const newEndDate = getDaysAgo(daysDiff);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  /**
   * Shift date range forward
   */
  const handleNext = () => {
    const daysDiff = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
    const currentEndDate = new Date(endDate);
    const mostRecentDate = availableDates.length > 0 ? new Date(availableDates[0]) : new Date();
    
    if (currentEndDate >= mostRecentDate) return; // Can't go beyond most recent snapshot
    
    const newStart = new Date(startDate);
    newStart.setDate(newStart.getDate() + daysDiff);
    const newEnd = new Date(endDate);
    newEnd.setDate(newEnd.getDate() + daysDiff);
    
    // Don't go beyond most recent snapshot
    if (newEnd > mostRecentDate) {
      setEndDate(availableDates.length > 0 ? availableDates[0] : getTodayDate());
    } else {
      setEndDate(newEnd.toISOString().split('T')[0]);
    }
    setStartDate(newStart.toISOString().split('T')[0]);
  };

  /**
   * Reset to default date range (last 2 weeks from most recent snapshot)
   */
  const handleToday = () => {
    if (availableDates.length > 0) {
      const mostRecentDate = availableDates[0];
      setEndDate(mostRecentDate);
      const startDateObj = new Date(mostRecentDate);
      startDateObj.setDate(startDateObj.getDate() - 14);
      setStartDate(startDateObj.toISOString().split('T')[0]);
    } else {
      // Fallback
      setStartDate(getDaysAgo(14));
      setEndDate(getTodayDate());
    }
  };

  /**
   * Parse lineup data and extract positions
   */
  const parseLineup = (lineupData) => {
    if (!lineupData) return { active: [], bench: [], il: [], minors: [] };

    // Handle the actual API structure with nested 'active', 'bench', 'DL', 'minors'
    const activePositions = lineupData.active || {};
    const bench = lineupData.bench || [];
    const il = lineupData.DL || lineupData.IL || []; // DL or IL
    const minors = lineupData.minors || [];

    const active = [];

    // Extract active roster positions from the 'active' object
    // Active positions now contain canonical player objects
    Object.keys(activePositions).forEach(key => {
      const playerData = activePositions[key];
      
      // Extract player info from canonical format
      const playerInfo = {
        position: key,
        playerId: playerData?.ids?.mlb || 'Unknown',
        name: playerData?.info?.full_name || 'Unknown Player',
        playerPosition: playerData?.info?.position || '',
        team: playerData?.info?.team || ''
      };
      
      active.push(playerInfo);
    });

    // Sort active positions logically
    const positionOrder = {
      'C': 0, '1B': 1, '2B': 2, 'SS': 3, '3B': 4, 
      'OF': 5, 'UTIL': 6, 'CI': 7, 'MI': 8, 'P': 9, 'SP': 10, 'RP': 11
    };
    
    active.sort((a, b) => {
      const posA = a.position.split('_')[0];
      const posB = b.position.split('_')[0];
      return (positionOrder[posA] || 99) - (positionOrder[posB] || 99);
    });

    return { active, bench, il, minors };
  };

  /**
   * Calculate differences between two lineups
   */
  const calculateDiff = (lineup1, lineup2) => {
    if (!lineup1 || !lineup2) return { changes: [], added: [], removed: [] };

    const changes = [];
    const added = [];
    const removed = [];

    const data1 = lineup1.lineup_data || {};
    const data2 = lineup2.lineup_data || {};

    // Get active positions from new canonical format
    const active1 = data1.active || {};
    const active2 = data2.active || {};

    // Check for position changes
    const allPositions = new Set([
      ...Object.keys(active1),
      ...Object.keys(active2)
    ]);

    allPositions.forEach(pos => {
      const player1Data = active1[pos];
      const player2Data = active2[pos];
      
      // Extract player IDs from canonical format
      const player1Id = player1Data?.ids?.mlb;
      const player2Id = player2Data?.ids?.mlb;

      if (player1Id && player2Id && player1Id !== player2Id) {
        changes.push({
          position: pos,
          oldPlayerId: player1Id,
          oldPlayerName: player1Data?.info?.full_name || 'Unknown',
          newPlayerId: player2Id,
          newPlayerName: player2Data?.info?.full_name || 'Unknown'
        });
      } else if (player2Id && !player1Id) {
        added.push({ 
          position: pos, 
          playerId: player2Id,
          name: player2Data?.info?.full_name || 'Unknown'
        });
      } else if (player1Id && !player2Id) {
        removed.push({ 
          position: pos, 
          playerId: player1Id,
          name: player1Data?.info?.full_name || 'Unknown'
        });
      }
    });

    return { changes, added, removed };
  };

  /**
   * Render position badge
   */
  const renderPositionBadge = (position) => {
    const displayPos = position.split('_')[0]; // "C_0" -> "C"
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.text.primary} ${dynastyTheme.classes.border.primary} border`}>
        {displayPos}
      </div>
    );
  };

  /**
   * Render timeline view
   */
  const renderTimeline = () => {
    if (lineups.length === 0) {
      return (
        <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
          <Calendar size={48} className={`mx-auto mb-4 ${dynastyTheme.classes.text.neutralLight}`} />
          <h3 className={`${dynastyTheme.components.heading.h3} mb-2`}>No Lineup Snapshots</h3>
          <p className={dynastyTheme.classes.text.neutralLight}>
            No lineup snapshots exist for this date range. Snapshots are captured daily when games start.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {lineups.map((lineup, index) => {
          const { active, bench, il } = parseLineup(lineup.lineup_data);
          const isLatest = index === 0;

          return (
            <div
              key={lineup.snapshot_id}
              className={`${dynastyTheme.components.card.base} ${isLatest ? dynastyTheme.classes.border.primaryBright : ''} p-6 ${dynastyTheme.classes.transition}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Calendar size={20} className={dynastyTheme.classes.text.primary} />
                  <h3 className={dynastyTheme.components.heading.h4}>
                    {formatDisplayDate(lineup.snapshot_date)}
                  </h3>
                  {isLatest && (
                    <span className={dynastyTheme.components.badge.success}>
                      Most Recent
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Clock size={16} className={dynastyTheme.classes.text.neutralLight} />
                  <span className={dynastyTheme.classes.text.neutralLight}>
                    {lineup.lock_time ? new Date(lineup.lock_time).toLocaleTimeString() : 'Unknown'}
                  </span>
                </div>
              </div>

              {/* Active Lineup */}
              <div className="mb-4">
                <h4 className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight} mb-3 uppercase tracking-wide`}>
                  Active Lineup ({active.length})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {active.map((slot, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded ${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.border.neutralLight} border`}
                    >
                      <div className="flex items-center space-x-2">
                        {renderPositionBadge(slot.position)}
                        <div className="flex flex-col">
                          <span className={dynastyTheme.classes.text.white}>
                            {slot.name}
                          </span>
                          <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                            {slot.playerPosition} {slot.team && `- ${slot.team}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bench */}
              {bench.length > 0 && (
                <div className="mb-4">
                  <h4 className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight} mb-3 uppercase tracking-wide`}>
                    Bench ({bench.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {bench.map((playerData, idx) => {
                      // Handle canonical format
                      const playerName = playerData?.info?.full_name || `Player ${playerData?.ids?.mlb || 'Unknown'}`;
                      return (
                        <span
                          key={idx}
                          className={`px-3 py-1 rounded ${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.text.neutralLight} text-sm`}
                        >
                          <PlayerLink mlbId={playerData?.ids?.mlb} name={playerName} style={{ color: 'inherit' }} />
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Injured List */}
              {il.length > 0 && (
                <div>
                  <h4 className={`text-sm font-semibold ${dynastyTheme.classes.text.neutralLight} mb-3 uppercase tracking-wide`}>
                    Injured List ({il.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {il.map((playerData, idx) => {
                      // Handle canonical format
                      const playerName = playerData?.info?.full_name || `Player ${playerData?.ids?.mlb || 'Unknown'}`;
                      return (
                        <span
                          key={idx}
                          className={`px-3 py-1 rounded ${dynastyTheme.classes.bg.error}/20 ${dynastyTheme.classes.text.error} border ${dynastyTheme.classes.border.error} text-sm`}
                        >
                          <PlayerLink mlbId={playerData?.ids?.mlb} name={playerName} style={{ color: 'inherit' }} />
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Render compare view
   */
  const renderCompare = () => {
    const lineup1 = lineups.find(l => l.snapshot_date === compareDate1);
    const lineup2 = lineups.find(l => l.snapshot_date === compareDate2);

    if (!lineup1 || !lineup2) {
      return (
        <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
          <GitCompare size={48} className={`mx-auto mb-4 ${dynastyTheme.classes.text.neutralLight}`} />
          <h3 className={`${dynastyTheme.components.heading.h3} mb-2`}>Select Dates to Compare</h3>
          <p className={dynastyTheme.classes.text.neutralLight}>
            Choose two dates from the dropdowns above to compare lineups.
          </p>
        </div>
      );
    }

    const diff = calculateDiff(lineup1, lineup2);

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`${dynastyTheme.components.card.base} p-6 text-center`}>
            <div className={`text-3xl font-bold mb-2 ${dynastyTheme.classes.text.primary}`}>
              {diff.changes.length}
            </div>
            <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              Position Changes
            </div>
          </div>
          <div className={`${dynastyTheme.components.card.base} p-6 text-center`}>
            <div className={`text-3xl font-bold mb-2 ${dynastyTheme.classes.text.success}`}>
              {diff.added.length}
            </div>
            <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              Players Added
            </div>
          </div>
          <div className={`${dynastyTheme.components.card.base} p-6 text-center`}>
            <div className={`text-3xl font-bold mb-2 ${dynastyTheme.classes.text.error}`}>
              {diff.removed.length}
            </div>
            <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              Players Removed
            </div>
          </div>
        </div>

        {/* Changes Detail */}
        {diff.changes.length > 0 && (
          <div className={dynastyTheme.components.card.base + ' p-6'}>
            <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>Position Changes</h3>
            <div className="space-y-3">
              {diff.changes.map((change, idx) => (
                <div key={idx} className={`flex items-center justify-between p-4 rounded ${dynastyTheme.classes.bg.neutral}`}>
                  <div className="flex items-center space-x-3">
                    {renderPositionBadge(change.position)}
                    <div className="flex items-center space-x-2">
                      <span className={dynastyTheme.classes.text.error}>{change.oldPlayerName}</span>
                      <span className={dynastyTheme.classes.text.neutralLight}>→</span>
                      <span className={dynastyTheme.classes.text.success}>{change.newPlayerName}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Added Players */}
        {diff.added.length > 0 && (
          <div className={dynastyTheme.components.card.base + ' p-6'}>
            <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>Players Added</h3>
            <div className="space-y-3">
              {diff.added.map((item, idx) => (
                <div key={idx} className={`flex items-center space-x-3 p-4 rounded ${dynastyTheme.classes.bg.success}/10 border ${dynastyTheme.classes.border.success}`}>
                  {renderPositionBadge(item.position)}
                  <span className={dynastyTheme.classes.text.success}>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Removed Players */}
        {diff.removed.length > 0 && (
          <div className={dynastyTheme.components.card.base + ' p-6'}>
            <h3 className={`${dynastyTheme.components.heading.h3} mb-4`}>Players Removed</h3>
            <div className="space-y-3">
              {diff.removed.map((item, idx) => (
                <div key={idx} className={`flex items-center space-x-3 p-4 rounded ${dynastyTheme.classes.bg.error}/10 border ${dynastyTheme.classes.border.error}`}>
                  {renderPositionBadge(item.position)}
                  <span className={dynastyTheme.classes.text.error}>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No changes */}
        {diff.changes.length === 0 && diff.added.length === 0 && diff.removed.length === 0 && (
          <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
            <Users size={48} className={`mx-auto mb-4 ${dynastyTheme.classes.text.success}`} />
            <h3 className={`${dynastyTheme.components.heading.h3} mb-2`}>No Changes</h3>
            <p className={dynastyTheme.classes.text.neutralLight}>
              The lineup remained identical between these two dates.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render loading state
  if (loading && datesLoading) {
    return (
      <div className="lineup-history-container p-6 max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <RefreshCw className="spinner mb-4" size={40} color={dynastyTheme.tokens.colors.primary} />
          <p className={dynastyTheme.classes.text.neutralLight}>Loading lineup history...</p>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="lineup-history-container p-6 max-w-7xl mx-auto">
      <TeamPageHeader
        title="Lineup History"
        logoUrl={userTeam?.team_logo_url}
        teamName={userTeam?.team_name}
        teamColors={userTeam?.team_colors}
        teamMotto={userTeam?.team_motto}
        badges={false}
      />

      {/* Controls */}
      <div className={`${dynastyTheme.components.card.base} p-6 mb-6`}>
        {/* View Mode Toggle */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('timeline')}
              className={`${viewMode === 'timeline' ? dynastyTheme.components.button.primary : dynastyTheme.components.button.secondary} px-4 py-2`}
            >
              <Clock size={16} className="inline mr-2" />
              Timeline
            </button>
            <button
              onClick={() => setViewMode('compare')}
              className={`${viewMode === 'compare' ? dynastyTheme.components.button.primary : dynastyTheme.components.button.secondary} px-4 py-2`}
            >
              <GitCompare size={16} className="inline mr-2" />
              Compare
            </button>
          </div>
        </div>

        {/* Date Range Controls (Timeline Mode) */}
        {viewMode === 'timeline' && (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevious}
                className={`${dynastyTheme.components.button.secondary} px-3 py-2`}
                title="Previous period"
              >
                <ChevronLeft size={20} />
              </button>
              
              <div className="flex items-center space-x-2">
                <label className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight}`}>
                  From:
                </label>
                <input
                  type="date"
                  value={startDate || ''}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate}
                  className={`${dynastyTheme.components.input} px-3 py-2`}
                />
              </div>

              <span className={dynastyTheme.classes.text.neutralLight}>to</span>

              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={endDate || ''}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  max={availableDates.length > 0 ? availableDates[0] : getTodayDate()}
                  className={`${dynastyTheme.components.input} px-3 py-2`}
                />
              </div>

              <button
                onClick={handleNext}
                className={`${dynastyTheme.components.button.secondary} px-3 py-2`}
                title="Next period"
                disabled={availableDates.length > 0 ? endDate >= availableDates[0] : endDate >= getTodayDate()}
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <button
              onClick={handleToday}
              className={`${dynastyTheme.components.button.secondary} px-4 py-2`}
            >
              <Calendar size={16} className="inline mr-2" />
              Last 2 Weeks
            </button>
          </div>
        )}

        {/* Compare Mode Dropdowns */}
        {viewMode === 'compare' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`${dynastyTheme.components.label} mb-2`}>
                First Date
              </label>
              <select
                value={compareDate1 || ''}
                onChange={(e) => setCompareDate1(e.target.value)}
                className={`${dynastyTheme.components.input} w-full px-3 py-2`}
              >
                <option value="">Select a date...</option>
                {availableDates.map(date => (
                  <option key={date} value={date}>
                    {formatDisplayDate(date)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={`${dynastyTheme.components.label} mb-2`}>
                Second Date
              </label>
              <select
                value={compareDate2 || ''}
                onChange={(e) => setCompareDate2(e.target.value)}
                className={`${dynastyTheme.components.input} w-full px-3 py-2`}
              >
                <option value="">Select a date...</option>
                {availableDates.map(date => (
                  <option key={date} value={date}>
                    {formatDisplayDate(date)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && !loading && (
        <div className={`${dynastyTheme.components.card.base} p-6 mb-6 border-l-4 ${dynastyTheme.classes.border.warning}`}>
          <div className="flex items-start space-x-3">
            <AlertCircle size={20} className={dynastyTheme.classes.text.warning} />
            <div>
              <h3 className={`font-semibold ${dynastyTheme.classes.text.white} mb-1`}>
                No Data Available
              </h3>
              <p className={dynastyTheme.classes.text.neutralLight}>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading && !datesLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <RefreshCw className="spinner mb-4" size={40} color={dynastyTheme.tokens.colors.primary} />
          <p className={dynastyTheme.classes.text.neutralLight}>Loading lineups...</p>
        </div>
      ) : (
        <>
          {viewMode === 'timeline' ? renderTimeline() : renderCompare()}
        </>
      )}
    </div>
  );
};

export default LineupHistory;
